/**
 * DIE GEMEINSAME BESTELLMASKE -- ein Formular, zwei Einstiege.
 *
 * „Bestellung hochladen" und „Manuell anlegen" enden HIER. Nicht in zwei
 * aehnlichen Masken, die auseinanderlaufen, sobald jemand eine davon
 * aendert: in DERSELBEN. Der Unterschied zwischen den beiden Wegen ist,
 * ob die Felder vorausgefuellt sind und ob ein Beleg daneben liegt --
 * und sonst nichts.
 *
 * DER UPLOAD IST KEINE BLACKBOX.
 *
 * Vor der Bestaetigung muss zu sehen sein, was erkannt wurde, was fehlt,
 * was zugeordnet wurde, was unsicher ist und welche Summen vorliegen.
 * Deshalb steht neben jedem vorausgefuellten Feld, WOHER der Wert kommt,
 * und ueber dem Formular eine Liste dessen, was nachzusehen ist.
 *
 * UND SIE LEGT NICHTS STILL AN. Aus dem Hochladen entsteht ein Vorschlag;
 * die Bestellung entsteht mit einem Knopf, auf dem „bestaetigen" steht.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import type {
  CaptureLineProposal, MetaResponse, PurchaseOrderCapture, SupplierItem,
  UpsertPurchaseOrderLineRequest,
} from "@homebase/shared";
import { api } from "../lib/api.ts";
import { useI18n, type I18n } from "../i18n/index.tsx";
import { errorText } from "../i18n/errors.ts";
import "./ProcurementPortal.css";

/** Eine Position im Entwurf -- so, wie die Maske sie fuehrt. */
interface Zeilenentwurf {
  /** Die Beziehung zum HOMEBASE-Artikel. Leer heisst: keine. */
  sa: string;
  artikelnummer: string;
  bezeichnung: string;
  menge: string;
  einheit: string;
  preis: string;
  /** Die Befunde aus der Extraktion -- sie bleiben sichtbar, bis jemand handelt. */
  befunde: readonly string[];
  /** Ob HOMEBASE fuer diese Zeile mehrere gleich gute Treffer hatte. */
  mehrdeutig: boolean;
}

const leereZeile = (): Zeilenentwurf => ({
  sa: "", artikelnummer: "", bezeichnung: "", menge: "", einheit: "", preis: "",
  befunde: [], mehrdeutig: false,
});

const ausVorschlag = (l: CaptureLineProposal): Zeilenentwurf => ({
  sa: l.match === null ? "" : String(l.match.supplierArticleId),
  artikelnummer: l.articleNumber ?? "",
  bezeichnung: l.description ?? "",
  menge: l.quantity === null ? "" : String(l.quantity),
  einheit: l.unit ?? "",
  preis: l.unitPrice ?? "",
  befunde: l.findings,
  mehrdeutig: l.ambiguous,
});

/** Betraege in ganzen Cent -- 14,70 mal 300 ist in Gleitkomma 4409,999999999999. */
function cent(roh: string): bigint | null {
  const s = roh.trim().replace(",", ".");
  if (s === "" || !/^\d+(\.\d+)?$/.test(s)) return null;
  const [g, n = ""] = s.split(".");
  return BigInt(g!) * 100n + BigInt((n + "00").slice(0, 2));
}
const alsBetrag = (c: bigint, lang: string): string =>
  (Number(c) / 100).toLocaleString(lang, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Die Summe der Positionen -- oder `null`, wenn keine Position einen
 * Preis traegt. Eine Null waere eine Aussage ueber den Auftrag.
 */
function positionssumme(zeilen: readonly Zeilenentwurf[]): bigint | null {
  let sum = 0n;
  let gab = false;
  for (const z of zeilen) {
    const p = cent(z.preis);
    const m = Number(z.menge);
    if (p === null || !Number.isFinite(m) || m <= 0) continue;
    sum += p * BigInt(Math.round(m));
    gab = true;
  }
  return gab ? sum : null;
}

export function OrderCapture({ meta, capture, i18n, onFertig, onAbbruch, onOpenPo }: {
  meta: MetaResponse;
  /** Der Vorschlag aus einem Beleg -- oder `null` fuer die manuelle Anlage. */
  capture: PurchaseOrderCapture | null;
  i18n: I18n;
  onFertig: (poId: number) => void;
  onAbbruch: () => void;
  onOpenPo: (poId: number) => void;
}): React.JSX.Element {
  const t = i18n.t;
  const [nummer, setNummer] = useState(capture?.dteNumber ?? "");
  const [supplier, setSupplier] = useState(
    capture?.supplierMatches.length === 1 ? String(capture.supplierMatches[0]!.id) : "");
  const [datum, setDatum] = useState(capture?.orderedAt ?? "");
  const [incoterm, setIncoterm] = useState(capture?.incoterm ?? "");
  const [waehrung, setWaehrung] = useState(capture?.currency ?? "");
  const [belegsumme, setBelegsumme] = useState(capture?.documentTotal ?? "");
  const [notiz, setNotiz] = useState("");
  const [zeilen, setZeilen] = useState<Zeilenentwurf[]>(
    capture === null || capture.lines.length === 0
      ? [leereZeile()] : capture.lines.map(ausVorschlag));
  const [artikel, setArtikel] = useState<readonly { id: number; label: string }[]>([]);
  const [fehler, setFehler] = useState<unknown>(null);
  const [speichert, setSpeichert] = useState(false);

  /* Die waehlbaren Beziehungen sind die des gewaehlten Lieferanten --
     eine Bestellung traegt nur Positionen ihres eigenen Partners. */
  const ladeArtikel = useCallback(async () => {
    if (supplier === "") { setArtikel([]); return; }
    try {
      const r = await api.supplierArticles(Number(supplier));
      setArtikel(r.items.map((a) => ({
        id: a.relationId,
        label: `${a.articleNumber} · ${a.shortDescription ?? ""}`.trim(),
      })));
    } catch { setArtikel([]); }
  }, [supplier]);
  useEffect(() => { void ladeArtikel(); }, [ladeArtikel]);

  const lieferanten: readonly SupplierItem[] = meta.suppliers;
  const summe = useMemo(() => positionssumme(zeilen), [zeilen]);
  const belegCent = cent(belegsumme);
  /*
   * ZWEI SUMMEN, UND DIE ABWEICHUNG IST DIE AUSKUNFT.
   *
   * HOMEBASE gleicht nichts an: weder wird die Belegsumme auf die
   * Positionen gerechnet noch umgekehrt. Es vermutet auch nichts --
   * kein Rabatt, keine Fracht, keine Gebuehr, keine Steuer.
   */
  const weichtAb = summe !== null && belegCent !== null && summe !== belegCent;

  const setzeZeile = (i: number, teil: Partial<Zeilenentwurf>): void =>
    setZeilen(zeilen.map((z, k) => (k === i ? { ...z, ...teil } : z)));

  async function bestaetigen(): Promise<void> {
    setSpeichert(true);
    setFehler(null);
    try {
      const positionen: UpsertPurchaseOrderLineRequest[] = zeilen
        .filter((z) => z.sa !== "" || z.bezeichnung.trim() !== "")
        .map((z) => ({
          supplierArticleId: z.sa === "" ? null : Number(z.sa),
          quantity: Number(z.menge),
          unitPriceAmount: z.preis.trim() === "" ? null : z.preis.trim().replace(",", "."),
          description: z.bezeichnung.trim() === "" ? null : z.bezeichnung.trim(),
          unit: z.einheit.trim() === "" ? null : z.einheit.trim(),
          articleNumberRaw: z.artikelnummer.trim() === "" ? null : z.artikelnummer.trim(),
        }));
      const kopf = {
        dteNumber: nummer.trim(), supplierId: Number(supplier), orderedAt: datum,
        incoterm: incoterm || null, currency: waehrung || null,
        note: notiz.trim() === "" ? null : notiz.trim(),
        documentTotal: belegsumme.trim() === "" ? null : belegsumme.trim().replace(",", "."),
      };
      if (capture !== null) {
        const po = await api.confirmCapture(capture.id, { ...kopf, lines: positionen });
        onFertig(po.id);
        return;
      }
      /*
       * DERSELBE VERTRAG, ZWEI AUFRUFE.
       *
       * Ohne Beleg gibt es keinen Vorgang zu bestaetigen -- die Anlage
       * IST hier die Bestaetigung. Die Felder und ihre Pruefungen sind
       * dieselben; es gibt keine zweite Bestellstruktur.
       */
      const po = await api.createPurchaseOrder(kopf);
      for (const z of positionen) await api.addPurchaseOrderLine(po.id, z);
      onFertig(po.id);
    } catch (e) { setFehler(e); } finally { setSpeichert(false); }
  }

  const befund = (code: string): React.JSX.Element => (
    <span key={code} className="plp__befund" data-befund={code}>
      {i18n.tCode("beleg", code, code)}
    </span>
  );

  return (
    <div className="plp" data-testid="plp-erfassung">
      <div className="sup__kopf">
        <h2 className="sup__titel">
          {capture === null ? t("plp.newOrder") : t("plp.captureTitle")}
        </h2>
        {capture !== null && (
          <span className="sup__tag" data-testid="plp-belegname">{capture.fileName}</span>
        )}
        <button className="btn btn--small" onClick={onAbbruch}>{t("common.cancel")}</button>
      </div>

      {fehler !== null && (
        <p className="log__error" role="alert" data-testid="plp-fehler">{errorText(i18n, fehler)}</p>
      )}

      {/*
        * WAS HOMEBASE IM BELEG GESEHEN HAT -- und was nicht.
        *
        * Der Kasten steht ueber dem Formular und nicht darunter: er ist
        * die Aufforderung nachzusehen, nicht die Quittung hinterher.
        */}
      {capture !== null && (
        <section className="log__group" data-testid="plp-belegbefunde">
          <h4 className="log__grouptitle">{t("plp.captureFindings")}</h4>
          <div className="log__rows">
            <div className="log__item"><span className="log__label">{t("plp.captureSource")}</span>
              <span className="log__vals">
                {t("plp.captureSourceValue", {
                  datei: capture.fileName,
                  wer: capture.uploadedBy ?? "—",
                  wann: capture.uploadedAt.slice(0, 10),
                })}
              </span></div>
            <div className="log__item"><span className="log__label">{t("plp.captureRawNumber")}</span>
              <span className="log__vals" data-testid="plp-rohnummer">
                {capture.dteNumberRaw ?? t("plp.captureNothing")}
              </span></div>
          </div>
          {capture.findings.length === 0 ? (
            <p className="log__hint" data-testid="plp-befundkeine">{t("plp.captureClean")}</p>
          ) : (
            <p className="plp__befunde">{capture.findings.map(befund)}</p>
          )}
          {/*
            * EINE BEREITS ERFASSTE BESTELLUNG WIRD GENANNT, nicht
            * ergaenzt und nicht ueberschrieben.
            */}
          {capture.duplicate !== null && (
            <p className="log__error" role="alert" data-testid="plp-dublette">
              {t("plp.captureDuplicate", { nr: capture.duplicate.dteNumber })}
              <button type="button" className="plp__punktziel"
                data-testid="plp-dubletteoeffnen"
                onClick={() => onOpenPo(capture.duplicate!.id)}>{t("plp.captureOpenExisting")}</button>
            </p>
          )}
        </section>
      )}

      <section className="log__group" data-testid="plp-erfassungkopf">
        <h4 className="log__grouptitle">{t("plp.order")}</h4>
        <div className="sup__kform">
          <label className="sup__kfeld"><span className="sup__klabel">{t("plp.dteNumber")}</span>
            <input className="log__input log__input--text" value={nummer} data-feld="dteNumber"
              aria-label={t("plp.dteNumber")} placeholder={t("plp.numberExample")}
              onChange={(e) => setNummer(e.target.value.toUpperCase())} /></label>
          <label className="sup__kfeld"><span className="sup__klabel">{t("sup.title")}</span>
            <select className="log__input" value={supplier} data-feld="supplierId"
              aria-label={t("sup.title")}
              onChange={(e) => setSupplier(e.target.value)}>
              <option value="">{t("plp.chooseSupplier")}</option>
              {lieferanten.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            {/*
              * DER NAME AUS DEM BELEG STEHT DANEBEN -- damit man sieht,
              * WORAUF sich der Vorschlag stuetzt. Zugeordnet wird nur,
              * was eindeutig ist; bei mehreren Treffern waehlt ein
              * Mensch, und bei keinem entsteht keine zweite
              * Lieferantenverwaltung.
              */}
            {capture !== null && (
              <span className="log__hint" data-testid="plp-lieferantvorschlag">
                {capture.supplierMatches.length === 0
                  ? t("plp.captureSupplierNone", { name: capture.supplierName ?? "—" })
                  : capture.supplierMatches.length === 1
                    ? t("plp.captureSupplierOne", { name: capture.supplierName ?? "—" })
                    : t("plp.captureSupplierMany", {
                        name: capture.supplierName ?? "—",
                        n: String(capture.supplierMatches.length),
                      })}
              </span>
            )}
          </label>
          <label className="sup__kfeld"><span className="sup__klabel">{t("plp.orderedAt")}</span>
            <input className="log__input log__input--text" type="date" value={datum}
              data-feld="orderedAt" aria-label={t("plp.orderedAt")}
              onChange={(e) => setDatum(e.target.value)} /></label>
          <label className="sup__kfeld"><span className="sup__klabel">{t("sup.incoterm")}</span>
            <select className="log__input" value={incoterm} data-feld="incoterm"
              aria-label={t("sup.incoterm")} onChange={(e) => setIncoterm(e.target.value)}>
              <option value="">{t("sup.optional")}</option>
              {meta.incoterms.map((w) => <option key={w.code} value={w.code}>{w.code}</option>)}
            </select></label>
          <label className="sup__kfeld"><span className="sup__klabel">{t("plp.currency")}</span>
            <select className="log__input" value={waehrung} data-feld="currency"
              aria-label={t("plp.currency")} onChange={(e) => setWaehrung(e.target.value)}>
              <option value="">{t("sup.optional")}</option>
              {meta.currencies.map((w) => <option key={w.code} value={w.code}>{w.code}</option>)}
            </select></label>
          <label className="sup__kfeld"><span className="sup__klabel">{t("plp.documentTotal")}</span>
            <input className="log__input log__input--text" value={belegsumme}
              data-feld="documentTotal" aria-label={t("plp.documentTotal")}
              placeholder={t("sup.optional")}
              onChange={(e) => setBelegsumme(e.target.value)} />
            <span className="log__hint">{t("plp.documentTotalHint")}</span></label>
          <label className="sup__kfeld sup__kfeld--breit">
            <span className="sup__klabel">{t("plp.note")}</span>
            <input className="log__input log__input--text" value={notiz} data-feld="note"
              aria-label={t("plp.note")} placeholder={t("sup.optional")}
              onChange={(e) => setNotiz(e.target.value)} /></label>
        </div>
      </section>

      <section className="log__group plp__positionen" data-testid="plp-erfassungzeilen">
        <h4 className="log__grouptitle">
          {t("plp.lines")}
          <button className="btn btn--small sup__plus" data-testid="plp-zeileneu"
            onClick={() => setZeilen([...zeilen, leereZeile()])}>{t("plp.addLine")}</button>
        </h4>
        <div className="sup__tablewrap">
          <table className="sup__table">
            <thead><tr>
              <th>{t("plp.article")}</th>
              <th>{t("plp.lineDescription")}</th>
              <th className="sup__num">{t("plp.quantity")}</th>
              <th>{t("plp.unit")}</th>
              <th className="sup__num">{t("plp.unitPrice")}</th>
              <th className="sup__num">{t("plp.lineTotal")}</th>
              <th />
            </tr></thead>
            <tbody>
              {zeilen.map((z, i) => {
                const p = cent(z.preis);
                const m = Number(z.menge);
                const wert = p === null || !Number.isFinite(m) || m <= 0
                  ? null : p * BigInt(Math.round(m));
                return (
                  <tr key={i} data-testid="plp-zeile">
                    <td>
                      {/*
                        * OHNE ARTIKEL IST EINE GUELTIGE WAHL. „Pallet
                        * fee" ist keine Leuchte, und eine Position,
                        * deren Artikel HOMEBASE noch nicht kennt, soll
                        * die Erfassung nicht aufhalten.
                        */}
                      <select className="log__input" value={z.sa} data-feld="supplierArticleId"
                        aria-label={t("plp.article")}
                        onChange={(e) => setzeZeile(i, { sa: e.target.value })}>
                        <option value="">{t("plp.noArticle")}</option>
                        {artikel.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
                      </select>
                      {z.artikelnummer !== "" && (
                        <span className="plp__pobez" data-testid="plp-rohnummerzeile">
                          {t("plp.captureRawItem", { nr: z.artikelnummer })}
                        </span>
                      )}
                      {z.mehrdeutig && (
                        <span className="plp__befund" data-befund="mehrdeutig">
                          {t("plp.captureAmbiguous")}
                        </span>
                      )}
                    </td>
                    <td>
                      <input className="log__input log__input--text" value={z.bezeichnung}
                        data-feld="description" aria-label={t("plp.lineDescription")}
                        onChange={(e) => setzeZeile(i, { bezeichnung: e.target.value })} />
                    </td>
                    <td className="sup__num">
                      <input className="log__input log__input--text plp__pofeld" type="number"
                        value={z.menge} data-feld="quantity" aria-label={t("plp.quantity")}
                        onChange={(e) => setzeZeile(i, { menge: e.target.value })} />
                    </td>
                    <td>
                      <input className="log__input log__input--text plp__pofeld" value={z.einheit}
                        data-feld="unit" aria-label={t("plp.unit")}
                        onChange={(e) => setzeZeile(i, { einheit: e.target.value })} />
                    </td>
                    <td className="sup__num">
                      <input className="log__input log__input--text plp__pofeld" value={z.preis}
                        data-feld="unitPriceAmount" aria-label={t("plp.unitPrice")}
                        onChange={(e) => setzeZeile(i, { preis: e.target.value })} />
                    </td>
                    <td className="sup__num" data-testid="plp-zeilenwert">
                      {wert === null ? "—" : `${alsBetrag(wert, i18n.lang)} ${waehrung}`}
                    </td>
                    <td>
                      {z.befunde.length > 0 && (
                        <span className="plp__befunde">{z.befunde.map(befund)}</span>
                      )}
                      <button type="button" className="bew__weg" data-testid="plp-zeileweg"
                        onClick={() => setZeilen(zeilen.filter((_, k) => k !== i))}>
                        {t("plp.removeLine")}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr data-testid="plp-summezeilen">
                <td colSpan={5}>{t("plp.orderValue")}</td>
                <td className="sup__num">
                  {summe === null ? t("plp.noValue") : `${alsBetrag(summe, i18n.lang)} ${waehrung}`}
                </td>
                <td />
              </tr>
              {belegCent !== null && (
                <tr data-testid="plp-summebeleg">
                  <td colSpan={5}>{t("plp.documentTotal")}</td>
                  <td className="sup__num">{`${alsBetrag(belegCent, i18n.lang)} ${waehrung}`}</td>
                  <td />
                </tr>
              )}
            </tfoot>
          </table>
        </div>
        {/*
          * DIE ABWEICHUNG WIRD GENANNT UND NICHT AUFGELOEST.
          *
          * Weder wird eine Ausgleichsposition erfunden noch eine der
          * beiden Summen an die andere angeglichen. Was der Unterschied
          * bedeutet, weiss nur, wer den Beleg gelesen hat.
          */}
        {weichtAb && (
          <p className="log__error" role="alert" data-testid="plp-summeabweichung">
            {t("plp.totalsDiffer", {
              summe: alsBetrag(summe!, i18n.lang),
              beleg: alsBetrag(belegCent!, i18n.lang),
              rest: alsBetrag(belegCent! > summe! ? belegCent! - summe! : summe! - belegCent!,
                i18n.lang),
            })}
          </p>
        )}
      </section>

      <div className="sup__kneu">
        <button className="btn btn--primary" data-testid="plp-bestaetigen"
          disabled={speichert || nummer.trim() === "" || supplier === "" || datum === ""}
          onClick={() => void bestaetigen()}>
          {speichert ? t("props.saving") : t("plp.confirmOrder")}
        </button>
        <button className="btn" disabled={speichert} onClick={onAbbruch}>
          {t("common.cancel")}
        </button>
        {/* Was fehlt, sagt der Knopf nicht -- es steht daneben. */}
        <span className="log__hint" data-testid="plp-bestaetigenhinweis">
          {t("plp.confirmHint")}
        </span>
      </div>
    </div>
  );
}
